import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Utensils, Plus, Gift } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { emptyPlan, STEPS, STEP_SCHEMA } from "@/lib/planModel";
import Header from "@/components/Header";
import FeedbackWidget from "@/components/FeedbackWidget";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Local step-level error boundary — a crash inside one Step must not blank
// the whole Wizard. Falls back to a visible message with a Reload button
// so athletes never see a mysterious black screen.
class StepErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, err: null }; }
  static getDerivedStateFromError(err) { return { hasError: true, err }; }
  componentDidCatch(err, info) { console.error("Wizard step crashed:", err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div data-testid="wizard-step-error" className="border border-[#FF9500]/40 bg-[#FF9500]/10 p-6">
          <p className="font-display text-xl uppercase tracking-tight text-[#FF9500] mb-2">This step hit a snag</p>
          <p className="text-sm text-zinc-200 mb-4">Something crashed on this step. Reloading usually fixes it.</p>
          <button onClick={() => window.location.reload()}
            className="bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wider px-4 py-2 text-xs transition-colors">
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import Step1Profile from "@/components/steps/Step1Profile";
import Step2Dietary from "@/components/steps/Step2Dietary";
import Step2Setup from "@/components/steps/Step2Setup";
import Step3Macros from "@/components/steps/Step3Macros";
import Step4Training from "@/components/steps/Step4Training";
import Step5Plan from "@/components/steps/Step5Plan";
import Step6Pathways from "@/components/steps/Step6Pathways";
import Step7Shopping from "@/components/steps/Step7Shopping";
import Step8Summary from "@/components/steps/Step8Summary";
import ExpressBuild from "@/components/wizard/ExpressBuild";

const PAYLOAD_KEYS = ["name", "weekStart", "unitSystem", "goal", "mealPlanning", "profile", "dietary", "macros",
  "prevWeek", "trainingWeek", "nextWeek", "raceWeek", "selections", "completions", "generated", "currentStep"];

// Fields the backend types as `Dict[str, Any]`. Any of these arriving as
// `null` triggers a 422 "Input should be a valid dictionary" — some older
// plan records in Mongo have nulls here, so we normalise on the way out.
const DICT_KEYS = ["profile", "dietary", "macros", "prevWeek", "trainingWeek",
  "nextWeek", "raceWeek", "selections", "completions", "generated"];

function toPayload(plan) {
  const p = {};
  PAYLOAD_KEYS.forEach((k) => { p[k] = plan[k]; });
  DICT_KEYS.forEach((k) => { if (p[k] == null) p[k] = {}; });
  return p;
}

// Step schema v1 → v2 (a "Setup" step was inserted at position 2). Plans saved
// mid-wizard under v1 land on the equivalent screen instead of a shifted one.
const OLD_TO_NEW_STEP = { 1: 1, 2: 3, 3: 4, 4: 5, 5: 6, 6: 7, 7: 8, 8: 9 };

function fromDoc(doc) {
  const base = emptyPlan();
  const profile = { ...base.profile, ...(doc.profile || {}) };
  const migrated = (profile.stepSchema || 1) < STEP_SCHEMA;
  const step = migrated
    ? (OLD_TO_NEW_STEP[doc.currentStep || 1] || 1)
    : (doc.currentStep || 1);
  profile.stepSchema = STEP_SCHEMA;
  // Spread `doc` first, then explicitly re-apply the dict-shaped fields so
  // an incoming `null` from an older Mongo record can't overwrite our
  // default object with `null` (which would then round-trip and 422).
  return {
    ...base,
    ...doc,
    planId: doc.id,
    currentStep: step,
    profile,
    dietary: { ...base.dietary, ...(doc.dietary || {}) },
    macros: { ...base.macros, ...(doc.macros || {}) },
    prevWeek: (doc.prevWeek && typeof doc.prevWeek === "object") ? doc.prevWeek : base.prevWeek,
    trainingWeek: (doc.trainingWeek && doc.trainingWeek.days)
      ? { ...doc.trainingWeek, days: doc.trainingWeek.days.map((d) => ({
          ...d, sessions: (d.sessions || []).map((s) => ({ intensity: "endurance", timing: "AM", ...s })) })) }
      : base.trainingWeek,
    nextWeek: (doc.nextWeek && typeof doc.nextWeek === "object") ? doc.nextWeek : base.nextWeek,
    raceWeek: { ...base.raceWeek, ...(doc.raceWeek || {}) },
    selections: doc.selections || {},
    completions: doc.completions || {},
    generated: doc.generated || base.generated,
  };
}

export default function Wizard() {
  const [plan, setPlan] = useState(emptyPlan);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [plans, setPlans] = useState([]);
  const [maxStep, setMaxStep] = useState(1);
  const loadedRef = useRef(false);
  const savingRef = useRef(false);
  const newPlanRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  // Coaches open an athlete's plan with ?athleteEmail= — the whole wizard then
  // reads and writes that athlete's plan through the coach endpoints.
  const athleteEmail = searchParams.get("athleteEmail") || "";
  const athleteRef = useRef(athleteEmail);
  useEffect(() => { athleteRef.current = athleteEmail; }, [athleteEmail]);
  const [regenerating, setRegenerating] = useState(false);
  // Tracks whether this wizard session began from Home's "Start a new week"
  // intent. Only used to collapse Biometrics + Goal & Fuelling on Step 1 —
  // "Continue editing" and "Pick a saved week" leave them fully expanded.
  const [isNewWeekSession, setIsNewWeekSession] = useState(false);
  const genRef = useRef(false);
  const planRef = useRef(plan);
  useEffect(() => { planRef.current = plan; }, [plan]);

  const { planTier } = useAuth();
  const isLite = false; // Lite tier retired Feb 2026 — coverage now via profile.usage
  // Only true (explicit YES) unlocks the meal-planning steps. `null`
  // (undecided) and `false` both skip Pathways + Shopping.
  const wantsMealPlanning = plan.mealPlanning === true;
  // "Tracker only" athletes bypass Training/Plan/Pathways/Shopping
  // entirely — they just want profile → dietary → macros → done so
  // their Tracker page has targets to work against.
  const trackerOnly = plan.profile?.usage === "tracker_only";
  const STEP_SEQUENCE = trackerOnly
    ? [1, 2, 3, 4, 9]
    : (wantsMealPlanning ? [1, 2, 3, 4, 5, 6, 7, 8, 9] : [1, 2, 3, 4, 5, 6, 9]);
  const visibleSteps = STEPS.filter((s) => STEP_SEQUENCE.includes(s.n));

  const patch = useCallback((section, values) => {
    setPlan((p) => ({ ...p, [section]: { ...p[section], ...values } }));
  }, []);
  const setTop = useCallback((key, value) => {
    setPlan((p) => ({ ...p, [key]: value }));
  }, []);

  // initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Coach editing an athlete: load THEIR plan and start at step 1 so the
        // whole plan can be reworked from the beginning.
        if (athleteEmail) {
          const { data } = await api.get(`/coach/athletes/${encodeURIComponent(athleteEmail)}/plan`);
          if (cancelled) return;
          if (data?.plan) {
            const p0 = fromDoc(data.plan);
            setPlan({ ...p0, currentStep: 1 });
            setMaxStep(STEP_SEQUENCE[STEP_SEQUENCE.length - 1]);
            toast.success(`Editing ${data.plan.profile?.name || athleteEmail}'s plan from the start`);
          } else {
            toast.error("That athlete doesn't have a plan yet — they need to build one first.");
            navigate("/coach");
          }
          return;
        }
        const { data } = await api.get("/plans");
        if (cancelled) return;
        setPlans(data || []);
        // Athlete arrived from Home with an explicit "start a new week" intent
        // — skip loading the latest plan and begin fresh. Prefill (name, DOB,
        // dietary etc.) still runs inside newPlan() once plans state exists.
        const intent = searchParams.get("intent");
        const loadId = searchParams.get("load");
        const startOverride = searchParams.get("start");
        if (intent === "new" || intent === "express") {
          setIsNewWeekSession(true);
          searchParams.delete("intent");
          searchParams.delete("start");
          setSearchParams(searchParams, { replace: true });
          // newPlan() reads from `plans` state (via closure) so schedule it
          // after this effect flushes and state is committed. Pass the
          // athlete's chosen Week Start from the Home date picker.
          setTimeout(() => { newPlanRef.current?.(startOverride || undefined); }, 0);
        } else if (loadId) {
          // Athlete arrived from Home's "Pick a saved week" list — jump
          // straight to that specific plan.
          searchParams.delete("load");
          setSearchParams(searchParams, { replace: true });
          const target = (data || []).find((p) => p.id === loadId);
          try {
            if (target) {
              // Editing an existing week always opens at Step 1 with every step
              // unlocked, so the whole plan can be reworked from the beginning.
              const p0 = fromDoc(target);
              setPlan({ ...p0, currentStep: 1 });
              setMaxStep(STEP_SEQUENCE[STEP_SEQUENCE.length - 1]);
            } else if (data && data.length) {
              const p0 = fromDoc(data[0]);
              setPlan({ ...p0, currentStep: 1 });
              setMaxStep(STEP_SEQUENCE[STEP_SEQUENCE.length - 1]);
            }
          } catch (loadErr) {
            // Never let a single malformed saved plan blank the whole
            // Wizard — bail out to a fresh plan and surface the error.
            console.error("Wizard: failed to load saved plan", loadErr);
            toast.error("Could not load that plan — starting a fresh one.");
          }
        } else if (data && data.length) {
          try {
            const p0 = fromDoc(data[0]); setPlan(p0); setMaxStep(p0.currentStep || 1);
          } catch (loadErr) {
            console.error("Wizard: failed to load latest plan", loadErr);
            toast.error("Could not load your latest plan — starting a fresh one.");
          }
        }
      } catch (e) {
        console.warn("Wizard: /plans fetch failed, starting fresh:", e?.message || e);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setTimeout(() => { loadedRef.current = true; }, 50);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useCallback(async (current) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = toPayload(current);
      const forAthlete = athleteRef.current;
      if (forAthlete) {
        // Editing someone else's plan — never create, only update theirs.
        if (!current.planId) return;
        await api.put(`/coach/athletes/${encodeURIComponent(forAthlete)}/plan`,
          { ...payload, planId: current.planId });
      } else if (current.planId) {
        await api.put(`/plans/${current.planId}`, payload);
      } else {
        const { data } = await api.post("/plans", payload);
        setPlan((p) => ({ ...p, planId: data.id }));
      }
      setDirty(false);
      dirtyRef.current = false;
    } catch (e) {
      // If the plan was deleted server-side (e.g. from another tab or the
      // admin console) our stale planId returns 404. Drop the id and let the
      // next save create a fresh plan instead of yelling at the athlete.
      if (e?.response?.status === 404 && current.planId) {
        setPlan((p) => ({ ...p, planId: null }));
        savingRef.current = false;
        setSaving(false);
        return;
      }
      // 401 is already handled by the axios interceptor (redirects to /login),
      // so skip the toast for that case.
      if (e?.response?.status === 401) {
        savingRef.current = false;
        setSaving(false);
        return;
      }
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map((x) => x?.msg).filter(Boolean).join(", ") : (e?.message || "Unknown error"));
      // Log the full error so DevTools shows exactly what the backend said.
      console.error("Auto-save failed", { status, detail, error: e });
      toast.error(status ? `Auto-save failed (${status})` : "Auto-save failed", {
        description: msg,
        action: { label: "Retry", onClick: () => save(planRef.current) },
        duration: 8000,
      });
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  }, []);

  // debounced autosave
  useEffect(() => {
    if (!loadedRef.current) return;
    setDirty(true);
    dirtyRef.current = true;
    const t = setTimeout(() => save(plan), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan]);

  // Flush any pending (debounced) save when leaving the wizard so quick edits
  // — e.g. deleting a training session then jumping straight to the Tracker —
  // are never lost to the cancelled debounce timer.
  useEffect(() => {
    return () => { if (loadedRef.current && dirtyRef.current) save(planRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generatePlan = useCallback(async () => {
    if (genRef.current) return;
    genRef.current = true;
    setRegenerating(true);
    try {
      const current = planRef.current;
      const cleanWeek = {
        ...current.trainingWeek,
        days: (current.trainingWeek.days || []).map((d) => ({
          ...d, sessions: (d.sessions || []).filter((s) => (parseInt(s.duration, 10) || 0) > 0),
        })),
      };
      const cleanRace = current.raceWeek.enabled
        ? { ...current.raceWeek, carbsPerHour: (parseInt(current.raceWeek.carbsPerHour, 10) || 0) || 60 }
        : current.raceWeek;
      const { data } = await api.post("/engine/generate", {
        profile: current.profile, dietary: current.dietary, trainingWeek: cleanWeek,
        goal: current.goal, macroOverride: current.macros.mode === "manual" ? current.macros : null,
        raceWeek: cleanRace,
        // Off => backend skips pathway/shopping/micros so the athlete gets
        // guidelines only. Undefined (undecided) still gets the full plan
        // so anyone who lands on Step 5 without picking gets something useful.
        mealPlanning: current.mealPlanning === false ? false : true,
      });
      // Guidelines-only mode returns dayPathways: null — no meal selections
      // to seed. Only iterate when the backend actually built pathways.
      const selections = {};
      (data.dayPathways || []).forEach((dp) => { selections[dp.index] = current.selections?.[dp.index] || "A"; });
      setPlan((p) => ({ ...p, generated: data, selections }));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map((x) => x?.msg).filter(Boolean).join(", ") : (e?.message || "Unknown error"));
      // eslint-disable-next-line no-console
      console.error("Generation failed", { status, detail, error: e });
      toast.error(status ? `Generation failed (${status})` : "Generation failed", {
        description: msg,
        duration: 10000,
      });
    } finally { setRegenerating(false); genRef.current = false; }
  }, []);

  // Auto-generate / auto-regenerate whenever an input changes. Fires on
  // first plan build too (used to only fire once a plan existed, which
  // meant the athlete had to click Generate on Step 5). Now the moment
  // profile weight is set + they have any training entered, the plan
  // materialises silently — no button press required.
  const inputSig = JSON.stringify({
    profile: plan.profile, dietary: plan.dietary, macros: plan.macros,
    goal: plan.goal, trainingWeek: plan.trainingWeek, raceWeek: plan.raceWeek,
  });
  useEffect(() => {
    if (!loadedRef.current) return;
    // Guard: need enough profile data for the engine to produce a valid plan.
    const p = planRef.current?.profile || {};
    if (!p.weight || !p.age || !p.height) return;
    const t = setTimeout(() => { generatePlan(); }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSig]);

  const [showErrors, setShowErrors] = useState(false);
  // Express mode: Home's "Quick build" sends ?intent=express. One short screen
  // instead of nine steps; the full wizard is one tap away.
  const [express, setExpress] = useState(() => searchParams.get("intent") === "express");
  const currentStep = plan.currentStep || 1;
  const setStep = (n) => {
    const clamped = Math.max(1, Math.min(STEPS.length, n));
    setPlan((p) => ({ ...p, currentStep: clamped }));
    setMaxStep((m) => Math.max(m, clamped));
    setShowErrors(false);
  };
  const goToStep = (n) => { setStep(n); window.scrollTo({ top: 0 }); };

  const canProceed = () => {
    if (currentStep === 1) {
      const { age, weight, height } = plan.profile;
      if (!age || !weight || !height) { toast.error("Please fill in the highlighted fields to continue"); return false; }
    }
    if (currentStep === 2) {
      if (plan.profile?.usage == null) { toast.error("Pick how you'll use SPC to continue"); return false; }
      if (plan.mealPlanning == null) { toast.error("Choose whether you want meal plans to continue"); return false; }
    }
    return true;
  };

  const goNext = () => {
    if (!canProceed()) { setShowErrors(true); return; }
    const i = STEP_SEQUENCE.indexOf(currentStep);
    setStep(STEP_SEQUENCE[Math.min(i + 1, STEP_SEQUENCE.length - 1)]);
    window.scrollTo({ top: 0 });
  };
  const goPrev = () => {
    const i = STEP_SEQUENCE.indexOf(currentStep);
    setStep(STEP_SEQUENCE[Math.max(i - 1, 0)]);
    window.scrollTo({ top: 0 });
  };
  const isLastStep = currentStep === STEP_SEQUENCE[STEP_SEQUENCE.length - 1];

  const navigate = useNavigate();
  const { hasTracker, trackerTrialUsed, refreshUser, user } = useAuth();
  const [addingTracker, setAddingTracker] = useState(false);
  const addTracker = async () => {
    setAddingTracker(true);
    try {
      await api.post("/payments/add-tracker");
      await refreshUser();
      toast.success("Tracker unlocked — enjoy!");
      navigate("/tracker");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) { toast.message("Choose a plan to add the Tracker"); navigate("/subscribe"); }
      else { toast.error(e?.response?.data?.detail || "Could not add the Tracker. Please try again."); setAddingTracker(false); }
    }
  };
  const startTrial = async () => {
    setAddingTracker(true);
    try {
      await api.post("/payments/start-tracker-trial");
      await refreshUser();
      toast.success("3-day Tracker trial started — enjoy!");
      navigate("/tracker");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) { toast.message("Choose a plan to start the trial"); navigate("/subscribe"); }
      else { toast.error(e?.response?.data?.detail || "Could not start your trial. Please try again."); setAddingTracker(false); }
    }
  };

  const newPlan = async (overrideWeekStart) => {
    loadedRef.current = false;    // Default Week Start to the coming Monday (or today if today is Monday) —
    // saves athletes touching the field on the vast majority of new plans.
    const nextMondayIso = (() => {
      const d = new Date();
      const dow = (d.getDay() + 6) % 7; // Mon=0 .. Sun=6
      const daysToAdd = dow === 0 ? 0 : 7 - dow;
      d.setDate(d.getDate() + daysToAdd);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    })();
    // Prefill from every prior plan so athletes don't retype their name,
    // DOB, sex, height, dietary + macro prefs on every new week. We look
    // across ALL saved plans newest-first and take the most-recent non-empty
    // value for each profile field. A previous partial or reset plan can no
    // longer wipe out biometrics the athlete already entered elsewhere.
    const sorted = [...plans].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    const latest = sorted[0];
    const base = emptyPlan();
    const isEmpty = (v) => v === undefined || v === null || v === "";
    const pickProfile = (field) => {
      for (const pl of sorted) {
        const v = pl.profile?.[field];
        if (!isEmpty(v)) return v;
      }
      return undefined;
    };
    if (latest) {
      // Assemble profile field-by-field from the freshest plan that has each
      // value — handles the "latest plan is partial" case Beta users hit.
      const stitched = {};
      const PROFILE_FIELDS = ["name", "sex", "dob", "age", "weight", "height",
        "weightUnit", "heightUnit", "activity", "goal", "goalWeight",
        "hydrationBaseline", "shoppingRegion", "cookingStyle",
        "householdSize"];
      for (const f of PROFILE_FIELDS) {
        const v = pickProfile(f);
        if (!isEmpty(v)) stitched[f] = v;
      }
      // Recompute age from DOB (if we have one) so it auto-ticks forward.
      if (stitched.dob) {
        const d = new Date(stitched.dob);
        if (!isNaN(d)) {
          const now = new Date();
          let a = now.getFullYear() - d.getFullYear();
          const m = now.getMonth() - d.getMonth();
          if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
          if (a >= 0 && a < 120) stitched.age = String(a);
        }
      }
      base.profile = { ...base.profile, ...stitched };
      // Same "look across all plans" strategy for dietary + macros so tweaks
      // survive an intervening partial plan.
      const pickTop = (section, field) => {
        for (const pl of sorted) {
          const v = pl?.[section]?.[field];
          if (!isEmpty(v)) return v;
        }
        return undefined;
      };
      const dietStitched = {};
      for (const f of Object.keys(base.dietary || {})) {
        const v = pickTop("dietary", f);
        if (!isEmpty(v)) dietStitched[f] = v;
      }
      base.dietary = { ...base.dietary, ...dietStitched };
      const macroStitched = {};
      for (const f of Object.keys(base.macros || {})) {
        const v = pickTop("macros", f);
        if (!isEmpty(v)) macroStitched[f] = v;
      }
      base.macros = { ...base.macros, ...macroStitched };
      base.goal = sorted.find((p) => !isEmpty(p.goal))?.goal || base.goal;
      base.unitSystem = sorted.find((p) => !isEmpty(p.unitSystem))?.unitSystem || plan.unitSystem;
    } else {
      base.unitSystem = plan.unitSystem;
    }
    // Weight fallback — if no prior plan carried a weight, pull the most
    // recent Tracker weigh-in. Silently no-op on network failure so we
    // never block a new-week flow on the sync.
    if (isEmpty(base.profile.weight)) {
      try {
        const { data } = await api.get("/tracker/weight/latest");
        if (data?.kg && data.kg > 0) base.profile.weight = data.kg;
      } catch { /* swallow — best-effort prefill */ }
    }
    // DOB, height and sex fallbacks — stored on the user account itself so
    // athletes with only one (partial) plan still get these carried forward.
    // Step 1 saves them there quietly the first time the athlete enters them.
    if (isEmpty(base.profile.dob) && user?.dob) base.profile.dob = user.dob;
    if (isEmpty(base.profile.height) && user?.height) base.profile.height = user.height;
    if (isEmpty(base.profile.sex) && user?.sex) base.profile.sex = user.sex;
    // Recompute age from DOB again after the fallback in case DOB was only
    // filled by the user-account fallback above.
    if (base.profile.dob && isEmpty(base.profile.age)) {
      const d = new Date(base.profile.dob);
      if (!isNaN(d)) {
        const now = new Date();
        let a = now.getFullYear() - d.getFullYear();
        const m = now.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
        if (a >= 0 && a < 120) base.profile.age = String(a);
      }
    }
    // Fill the plan's profile name from the signed-in user's account name if
    // the previous plan didn't already carry it. Athletes set their full name
    // once at signup (or later from Account Settings) — no need to re-enter it.
    if (!base.profile.name && user?.name) base.profile.name = user.name;
    const finalWeekStart = overrideWeekStart || nextMondayIso;
    base.weekStart = finalWeekStart;
    // Default the plan name to "w/c <date>" so it's meaningful the moment
    // the athlete lands. They can override on Step 1 if they want a nickname
    // (e.g. "Ironman Taper", "Race Week").
    const derivedName = (() => {
      const d = new Date(`${finalWeekStart}T00:00:00`);
      if (isNaN(d.getTime())) return "Untitled week";
      return `w/c ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
    })();
    base.name = derivedName;
    base.currentStep = 1;
    setPlan(base);
    // Unlock every step so athletes can freely explore forward — landing on
    // Step 1 they can edit 1/2 and peek at 3+ before committing. Wizard still
    // guards essentials via `canProceed` before generating a plan.
    setMaxStep(STEP_SEQUENCE[STEP_SEQUENCE.length - 1]);
    setDirty(false);
    setTimeout(() => { loadedRef.current = true; }, 50);
    window.scrollTo({ top: 0 });
    toast.success(latest ? `Started a new plan for w/c ${finalWeekStart} — profile prefilled` : `Started a new plan for w/c ${finalWeekStart}`);
  };
  // Expose newPlan to the initial-load effect so the ?intent=new URL param
  // (from Home's "Start a new week" pop-up) can trigger it once plans load.
  useEffect(() => { newPlanRef.current = newPlan; });

  const refreshPlans = useCallback(async () => {
    try { const { data } = await api.get("/plans"); setPlans(data || []); } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Could not refresh plans list:", e?.message || e); }
  }, []);

  const loadPlan = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/plans/${id}`);
      loadedRef.current = false;
      // Open any loaded plan at the start, fully unlocked.
      setPlan({ ...fromDoc(data), currentStep: 1 });
      setMaxStep(STEP_SEQUENCE[STEP_SEQUENCE.length - 1]);
      setDirty(false);
      setTimeout(() => { loadedRef.current = true; }, 50);
      window.scrollTo({ top: 0 });
      toast.success(`Loaded "${data.name || "plan"}"`);
    } catch (e) { toast.error("Could not load plan"); }
  }, []);

  const duplicatePlan = useCallback(async () => {
    try {
      const payload = toPayload({ ...plan, name: `${plan.name || "Plan"} (copy)` });
      const { data } = await api.post("/plans", payload);
      loadedRef.current = false;
      setPlan(fromDoc(data));
      setMaxStep(STEP_SEQUENCE[STEP_SEQUENCE.length - 1]);
      setDirty(false);
      setTimeout(() => { loadedRef.current = true; }, 50);
      refreshPlans();
      window.scrollTo({ top: 0 });
      toast.success("Duplicated — you're now editing the copy");
    } catch (e) { toast.error("Could not duplicate plan"); }
  }, [plan, refreshPlans]);

  const renamePlan = useCallback(async (id, newName) => {
    try {
      if (plan.planId === id) {
        setTop("name", newName);
      } else {
        const doc = plans.find((p) => p.id === id);
        if (!doc) return;
        await api.put(`/plans/${id}`, { ...toPayload(fromDoc(doc)), name: newName });
      }
      refreshPlans();
      toast.success("Plan renamed");
    } catch (e) { toast.error("Could not rename plan"); }
  }, [plan.planId, plans, setTop, refreshPlans]);

  const deletePlan = useCallback(async (id) => {
    try {
      await api.delete(`/plans/${id}`);
      toast.success("Plan deleted");
      if (plan.planId === id) newPlan();
      refreshPlans();
    } catch (e) { toast.error("Could not delete plan"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.planId, refreshPlans]);

  const stepProps = { plan, setPlan, patch, setTop, goNext, goPrev, setStep, showErrors, onGenerate: generatePlan, regenerating, isLite, isNewWeekSession };

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1Profile {...stepProps} />;
      case 2: return <Step2Setup {...stepProps} />;
      case 3: return <Step2Dietary {...stepProps} />;
      case 4: return <Step3Macros {...stepProps} />;
      case 5: return <Step4Training {...stepProps} />;
      case 6: return <Step5Plan {...stepProps} />;
      case 7: return <Step6Pathways {...stepProps} />;
      case 8: return <Step7Shopping {...stepProps} />;
      case 9: return <Step8Summary {...stepProps} />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#0A0A0A]" data-testid="wizard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#E2E30A]" />
        <p className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Loading your plan…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header
        currentStep={currentStep}
        maxStep={maxStep}
        onStepClick={goToStep}
        steps={visibleSteps}
        saving={saving}
        dirty={dirty}
        onNewPlan={newPlan}
        plans={plans}
        activePlanId={plan.planId}
        onLoadPlan={loadPlan}
        onRefreshPlans={refreshPlans}
        onDuplicate={duplicatePlan}
        onRenamePlan={renamePlan}
        onDeletePlan={deletePlan}
        coachEditing={athleteEmail}
      />

      {athleteEmail && (
        <div className="bg-[#34C759]/[0.08] border-b border-[#34C759]/40 px-4 sm:px-6 py-2.5 no-print" data-testid="coach-editing-banner">
          <p className="max-w-6xl mx-auto text-[11px] uppercase tracking-[0.18em] text-[#34C759] font-bold">
            Coach mode · editing {plan.profile?.name || athleteEmail}'s plan — changes save to their account
          </p>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 overflow-x-hidden">
        {express ? (
          <ExpressBuild plan={plan} setPlan={setPlan}
            onBuilt={() => { setExpress(false); window.scrollTo({ top: 0 }); toast.success("Your week is built — have a look, then tweak anything you like."); }}
            onFullWizard={() => { setExpress(false); setStep(1); window.scrollTo({ top: 0 }); }} />
        ) : (
        <>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="step-enter"
          >
            <StepErrorBoundary>{renderStep()}</StepErrorBoundary>
          </motion.div>
        </AnimatePresence>

        {/* Nav footer */}
        <div className="flex items-center justify-between mt-12 pt-6 border-t border-white/10 no-print">
          <Button
            data-testid="wizard-prev-btn" variant="ghost" onClick={goPrev} disabled={currentStep === 1}
            className="rounded-none text-zinc-200 hover:text-white hover:bg-white/5 uppercase tracking-wide disabled:opacity-30"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <span className="text-xs text-zinc-200 font-mono">STEP {String(STEP_SEQUENCE.indexOf(currentStep) + 1).padStart(2, "0")} / {String(STEP_SEQUENCE.length).padStart(2, "0")} — {STEPS[currentStep - 1].label}</span>
          {!isLastStep ? (
            <Button
              data-testid="wizard-next-btn" onClick={goNext}
              className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black font-semibold uppercase tracking-wide transition-colors"
            >
              Next <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : hasTracker ? (
            <Button
              data-testid="wizard-go-tracker-btn" onClick={() => navigate("/tracker")}
              className="rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black font-semibold uppercase tracking-wide transition-colors"
            >
              <Utensils className="h-4 w-4 mr-2" /> Go to Tracker
            </Button>
          ) : !trackerTrialUsed ? (
            <Button
              data-testid="wizard-trial-tracker-btn" onClick={startTrial} disabled={addingTracker}
              className="rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black font-semibold uppercase tracking-wide transition-colors"
            >
              {addingTracker ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gift className="h-4 w-4 mr-2" />} Try Tracker Free · 3 Days
            </Button>
          ) : (
            <Button
              data-testid="wizard-add-tracker-btn" onClick={addTracker} disabled={addingTracker}
              className="rounded-none bg-[#4C9AFF] hover:bg-[#3a86e6] text-black font-semibold uppercase tracking-wide transition-colors"
            >
              {addingTracker ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Add Tracker
            </Button>
          )}
        </div>
        </>
        )}
      </main>

      <FeedbackWidget />
    </div>
  );
}
